import { Dispatch, SetStateAction, use, useState } from 'react';
import { HttpStatusCode, isAxiosError } from 'axios';
import { AlarmStatus } from 'shared/components/alarm-card/enums/alarm-status';
import { AlarmType } from 'shared/components/alarm-card/enums/alarm-type';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';

import { useHistoryContextProvider } from 'features/history/providers/history-provider';
import { HandleEmergencyAlarmCommand } from '../domain/models/update-emergency-alarm-status-command';
import { useHandleEmergencyAlarm } from '../domain/queries/emergency-alarm-queries';

export interface IEmergencyAlarmProviderHook {
  handleEmergencyAlarm: () => void;
  isOpen: boolean;
  isHandlingEmergencyAlarm: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  canHandleEmergencyAlarm: boolean;
}

const useEmergencyAlarm = (): IEmergencyAlarmProviderHook => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { readEmergencyAlarmData, refresh } = useHistoryContextProvider();

  const { mutate: mutateHandleEmergencyAlarm, isPending: handleEmergencyAlarmIsPending } = useHandleEmergencyAlarm();

  const handleEmergencyAlarm = () => {
    if (!readEmergencyAlarmData || !canHandleEmergencyAlarm) {
      return;
    }

    const command: HandleEmergencyAlarmCommand = {
      id: readEmergencyAlarmData.id,
      status: AlarmStatus.Handled,
    };

    mutateHandleEmergencyAlarm(command, {
      onSuccess: () => {
        setIsOpen(false);
        refresh();
      },
      onError: (error) => {
        if (isAxiosError(error) && error.response?.status === HttpStatusCode.Conflict) {
          setIsOpen(false);
          refresh();
          return;
        }

        setErrorMessage(error);
        refresh();
      },
    });
  };

  const canHandleEmergencyAlarm = readEmergencyAlarmData
    ? readEmergencyAlarmData.alarmType === AlarmType.EmergencyAlarm &&
      (readEmergencyAlarmData.status === AlarmStatus.Reported || readEmergencyAlarmData.status === AlarmStatus.Accepted)
    : false;

  return {
    isOpen,
    isHandlingEmergencyAlarm: handleEmergencyAlarmIsPending,
    setIsOpen,
    handleEmergencyAlarm,
    canHandleEmergencyAlarm,
  };
};

export default useEmergencyAlarm;
